import type { LscLeague, LscMember } from './types';

/** One Discord DM. Edit this string; Copy message substitutes {name}, {team}, and {link}. */
export const MEMBER_INVITE_DM_TEMPLATE = `Yo {name} — I've been building something for the league and I want you in on it early.

It's called League Stream Center. The point: when you go live with a league game, the league page shows it automatically — who's streaming, what matchup, what week. No more dropping a link in Discord and hoping somebody's around. People can actually find your games while they're happening.

And every game you stream gets filed under the real matchup, so we end up with an actual record of the season instead of a pile of random VODs nobody can sort through.

It's early and I need a few guys to help me prove it works. You're one of them.

Two minutes to get set up:

1. Open this: {link}
2. Sign in with Twitch — that's it, no password to make. Use the account you actually stream from.
3. Make sure it shows you on the {team}. If it's wrong, tell me
4. In Twitch, flip on Settings → Stream → Store past broadcasts

That link is yours alone — works once, expires in 14 days.`;

export const GOOGLE_MEMBER_INVITE_DM_TEMPLATE = `Yo {name} — I've been building something for the league and I want you in on it early.

It's called League Stream Center. The point: when you go live with a league game, the league page shows it automatically — who's streaming, what matchup, what week. No more dropping a link in Discord and hoping somebody's around. People can actually find your games while they're happening.

And every game you stream gets filed under the real matchup, so we end up with an actual record of the season instead of a pile of random VODs nobody can sort through.

It's early and I need a few guys to help me prove it works. You're one of them.

Two minutes to get set up:

1. Open this: {link}
2. Sign in with Google — that's it, no password to make.
3. Make sure it shows you on the {team}. If it's wrong, tell me

That link is yours alone — works once, expires in 14 days.`;

export const UNASSIGNED_JOIN_COPY =
  "Joins members without a team. You'll assign teams afterwards.";

type ImportLeague = Pick<
  LscLeague,
  'external_neonsportz_slug' | 'schedule_source' | 'last_sync_source'
> | null | undefined;

export function leagueHasNeonSportzImport(league: ImportLeague): boolean {
  if (!league) return false;
  if (String(league.external_neonsportz_slug || '').trim()) return true;
  const source = league.schedule_source;
  const provider = String(source?.provider || '').trim().toLowerCase();
  if (source?.enabled && (provider === 'neonsportz' || String(source.provider_slug || '').trim())) {
    return true;
  }
  return String(league.last_sync_source || '').trim().toLowerCase() === 'neonsportz';
}

export function memberTwitchHandle(member: Pick<LscMember, 'twitch_login' | 'twitch_user_id'>): string {
  const login = String(member.twitch_login || '').trim();
  if (login) return login.startsWith('@') ? login : `@${login}`;
  const id = String(member.twitch_user_id || '').trim();
  return id ? `id:${id}` : '';
}

export function memberIsTwitchConnected(
  member: Pick<LscMember, 'status' | 'twitch_login' | 'twitch_user_id'>
): boolean {
  if (member.status === 'inactive') return false;
  if (member.status && member.status !== 'active') return false;
  return Boolean(memberTwitchHandle(member));
}

const PLATFORM_LABELS: Record<string, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
};

export function streamPlatformLabel(platform: string): string {
  const key = platform.trim().toLowerCase();
  if (PLATFORM_LABELS[key]) return PLATFORM_LABELS[key];
  if (!key) return '';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

type RosterMember = Pick<
  LscMember,
  | 'status'
  | 'user_uid'
  | 'twitch_login'
  | 'twitch_user_id'
  | 'stream_platform'
  | 'stream_accounts'
  | 'roster_platform'
  | 'roster_handle'
  | 'roster_account_bound'
  | 'detection_covered'
  | 'deactivation_reason'
  | 'current_side_label'
>;

function hasRosterPlatform(member: RosterMember): boolean {
  return Object.prototype.hasOwnProperty.call(member, 'roster_platform');
}

export function memberRosterPlatform(member: RosterMember): string | null {
  if (hasRosterPlatform(member)) {
    const explicit = String(member.roster_platform || '').trim().toLowerCase();
    return explicit || null;
  }
  const platform = String(member.stream_platform || '').trim().toLowerCase();
  if (platform === 'twitch' || platform === 'youtube') return platform;
  if (member.twitch_login || member.twitch_user_id) return 'twitch';
  const accounts = member.stream_accounts || [];
  const primary = accounts.find((account) => account?.primary) || accounts[0];
  const fromAccount = String(primary?.platform || '').trim().toLowerCase();
  if (fromAccount === 'twitch' || fromAccount === 'youtube') return fromAccount;
  return null;
}

export function memberRosterHandle(member: RosterMember): string {
  if (Object.prototype.hasOwnProperty.call(member, 'roster_handle')) {
    return String(member.roster_handle || '').trim();
  }
  const platform = memberRosterPlatform(member);
  if (platform === 'twitch') return memberTwitchHandle(member);
  if (platform === 'youtube') {
    const accounts = member.stream_accounts || [];
    const primary = accounts.find((account) => account?.primary) || accounts[0];
    const handle = String(primary?.handle || '').trim();
    if (!handle) return '';
    return handle.startsWith('@') ? handle : `@${handle}`;
  }
  return '';
}

export function memberRosterAccountBound(member: RosterMember): boolean {
  if (typeof member.roster_account_bound === 'boolean') return member.roster_account_bound;
  return Boolean(memberRosterHandle(member));
}

export function memberIsClaimedSeat(member: Pick<LscMember, 'status' | 'user_uid'>): boolean {
  return member.status === 'active' && Boolean(String(member.user_uid || '').trim());
}

export function memberIsDetectable(member: RosterMember): boolean {
  return (
    memberIsClaimedSeat(member) &&
    member.detection_covered === true &&
    memberRosterAccountBound(member)
  );
}

export function memberStreamsCopy(member: RosterMember): string {
  const platform = memberRosterPlatform(member);
  if (!platform) return 'No platform set';
  const label = streamPlatformLabel(platform);
  if (!memberRosterAccountBound(member)) return `${label} · channel not set`;
  const handle = memberRosterHandle(member);
  const coverage = member.detection_covered === true ? 'detected' : 'not detected yet';
  return handle ? `${label} ${handle} · ${coverage}` : `${label} · ${coverage}`;
}

export function memberClaimCopy(
  member: RosterMember,
  invite?: { status?: string | null; valid?: boolean; expires_at?: string | null } | null
): string {
  if (member.deactivation_reason === 'commissioner_removed') return 'Removed by commissioner';
  if (memberIsClaimedSeat(member)) {
    const handle = memberTwitchHandle(member);
    return handle ? `Claimed ${handle}` : 'Claimed · Google';
  }
  if (invite?.status === 'active' && invite.valid) {
    const expiry = formatInviteExpiresIn(invite.expires_at);
    return `Invite sent · ${expiry.label}`;
  }
  return 'Not claimed';
}

export interface MemberRosterCounts {
  importedUnclaimed: number;
  claimed: number;
  twitchLinked: number;
}

export interface LiveDetectionConnection {
  connected: number;
  twitch_linked: number;
  not_connected_names?: string[];
}

/** Fourth members-step count. Enabled means event delivery or Helix confirmation. */
export function liveDetectionConnectionCopy(
  connection: LiveDetectionConnection | null | undefined
): string | null {
  if (!connection) return null;
  const connected = Number(connection.connected) || 0;
  const linked = Number(connection.twitch_linked) || 0;
  const base = `Connected for live detection: ${connected} of ${linked} Twitch-linked`;
  const names = (connection.not_connected_names || []).map((name) => String(name || '').trim()).filter(Boolean);
  if (!names.length) return base;
  return `${base}. Not connected: ${names.join(', ')}`;
}

/** Display counts for the members step. Twitch linked is independent of claim status. */
export function memberRosterCounts(members: LscMember[]): MemberRosterCounts {
  let importedUnclaimed = 0;
  let claimed = 0;
  let twitchLinked = 0;
  for (const member of members) {
    const status = String(member.status || '').trim().toLowerCase();
    if (status === 'imported_unclaimed') {
      importedUnclaimed += 1;
    } else if (status === 'active' || String(member.user_uid || '').trim()) {
      claimed += 1;
    }
    if (String(member.twitch_user_id || '').trim() || String(member.twitch_login || '').trim()) {
      twitchLinked += 1;
    }
  }
  return { importedUnclaimed, claimed, twitchLinked };
}

function teamDetectionCause(group: LscMember[]): 'ok' | 'not_claimed' | 'no_account' | string {
  if (group.some((member) => memberIsDetectable(member))) return 'ok';
  const claimed = group.filter((member) => memberIsClaimedSeat(member));
  if (!claimed.length) return 'not_claimed';
  const uncovered = claimed.find((member) => {
    const platform = memberRosterPlatform(member);
    return Boolean(platform) && member.detection_covered !== true;
  });
  if (uncovered) return memberRosterPlatform(uncovered) || 'no_account';
  return 'no_account';
}

export function teamConnectionHeadline(members: LscMember[]): string {
  const teams = new Map<string, LscMember[]>();
  for (const member of members) {
    if (member.status === 'inactive') continue;
    const team = String(member.current_side_label || '').trim();
    if (!team) continue;
    const key = team.toLowerCase();
    const group = teams.get(key) || [];
    group.push(member);
    teams.set(key, group);
  }
  const total = teams.size;
  if (total === 0) {
    return 'No teams are assigned yet. A live stream can\'t bind to a game until a member is on a team.';
  }
  let notClaimed = 0;
  let noAccount = 0;
  const unsupported = new Map<string, number>();
  teams.forEach((group) => {
    const cause = teamDetectionCause(group);
    if (cause === 'ok') return;
    if (cause === 'not_claimed') {
      notClaimed += 1;
      return;
    }
    if (cause === 'no_account') {
      noAccount += 1;
      return;
    }
    unsupported.set(cause, (unsupported.get(cause) || 0) + 1);
  });
  const missing = notClaimed + noAccount + Array.from(unsupported.values()).reduce((sum, count) => sum + count, 0);
  if (missing === 0) return 'Every team\'s games can be detected.';
  const parts: string[] = [];
  if (notClaimed) parts.push(`${notClaimed} not claimed`);
  Array.from(unsupported.keys())
    .sort()
    .forEach((platform) => {
      parts.push(`${unsupported.get(platform)} on ${streamPlatformLabel(platform)}`);
    });
  if (noAccount) parts.push(`${noAccount} claimed with no account`);
  return `${missing} of ${total} teams' games can't be detected yet — ${parts.join(', ')}.`;
}

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function formatInviteExpiresIn(
  iso: string | null | undefined,
  now: Date = new Date()
): { label: string; title: string } {
  if (!iso) return { label: 'link valid', title: '' };
  const expires = new Date(iso);
  if (Number.isNaN(expires.getTime())) return { label: 'link valid', title: '' };
  const title = expires.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const remaining = expires.getTime() - now.getTime();
  if (remaining <= 0) return { label: 'expired', title };
  const dayDiff = Math.round((startOfLocalDay(expires) - startOfLocalDay(now)) / 86400000);
  if (dayDiff <= 0) return { label: 'expires today', title };
  if (dayDiff === 1) return { label: 'expires in 1 day', title };
  return { label: `expires in ${dayDiff} days`, title };
}

export function formatMemberInviteMessage(input: {
  displayName: string;
  teamLabel?: string | null;
  leagueName?: string | null;
  claimUrl: string;
  signInMethod?: 'google' | 'twitch';
}): string {
  const name = input.displayName.trim() || 'there';
  const team = String(input.teamLabel || '').trim() || 'your team';
  const template = input.signInMethod === 'google'
    ? GOOGLE_MEMBER_INVITE_DM_TEMPLATE
    : MEMBER_INVITE_DM_TEMPLATE;
  return template.split('{name}')
    .join(name)
    .split('{team}')
    .join(team)
    .split('{link}')
    .join(input.claimUrl);
}

export function formatBulkInviteClipboard(
  rows: { display_name: string; team_label?: string | null; claim_url: string }[]
): string {
  return rows
    .map((row) => `${row.display_name} — ${row.team_label || 'No team'} — ${row.claim_url}`)
    .join('\n');
}
